// Billing — the account app's client for the server's `/api/billing/*`
// surface (billing-lifecycle.md §4.3; billing-signup.md §5.3).
//
// Two halves. The signup half is public: it runs before a restaurant exists,
// so there is no merchant session to carry. The restaurant half is owner-
// gated and follows the `services/pulse.ts` shape — one silent retry through
// `ensureMerchantSession` on a 401.

import { serverFetch } from '@/lib/serverFetch'
import { ApiError } from '@/lib/apiError'
import { ensureMerchantSession } from './merchantAuth'
import type { LoyaltyServiceContext } from './loyalty'
import type { BillingInterval } from '@/lib/planIntent'

export type { BillingInterval }

export type Plan = 'basic' | 'premium'
export type PlanSource = 'stripe' | 'comp' | 'none'
export type CompSource = 'grandfathered' | 'chamber' | 'internal'

/** Wire shape of `GET /api/billing/:restaurant_pubkey` (snake_case). */
export interface PlanSnapshot {
  plan: Plan
  source: PlanSource
  status: string | null
  interval: BillingInterval | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  /** When a cancellation is scheduled, the instant Premium ends. */
  cancel_at: string | null
  comp_source: CompSource | null
  comp_expires_at: string | null
  has_stripe_customer: boolean
}

export interface CheckoutStatus {
  /** The Checkout did what it was opened for: paid, or the card passed its check. */
  paid: boolean
  claimed: boolean
  plan: Plan | null
}

/**
 * Where a person stands before they have a restaurant
 * (`GET /api/billing/signup/:personal_pubkey`, snake_case). `none` means no
 * Checkout yet; `checkout` one not finished; `paid` finished but no
 * restaurant asked for; the last three are the request's own states.
 */
export type SignupState = 'none' | 'checkout' | 'paid' | 'pending' | 'approved' | 'rejected'

export interface SignupStatus {
  state: SignupState
  plan: Plan | null
  checkout_session_id: string | null
  restaurant_name: string | null
  google_place_id: string | null
  address: string | null
  requested_at: string | null
  /** The operator's words, only when rejected. */
  reason: string | null
  refunded: boolean
}

// ── Signup half: no restaurant yet ───────────────────────────────────────

export function createSignupCheckout(input: {
  personalPubkey: string
  email: string | null
  plan: Plan
  /** Premium only; a Basic sign-up runs a card check with no price. */
  interval?: BillingInterval
}): Promise<{ url: string }> {
  return serverFetch<{ url: string }>('/api/billing/signup-checkout', {
    method: 'POST',
    body: JSON.stringify({
      personal_pubkey: input.personalPubkey,
      email: input.email,
      plan: input.plan,
      ...(input.plan === 'premium' ? { interval: input.interval } : {}),
    }),
  })
}

export function fetchSignupCheckoutStatus(sessionId: string): Promise<CheckoutStatus> {
  return serverFetch<CheckoutStatus>(`/api/billing/signup-checkout/${encodeURIComponent(sessionId)}`)
}

/**
 * The wizard, after payment and the search: this is my restaurant. Nothing
 * is provisioned; the request waits for an operator (sign-up approval PRD).
 */
export function submitSignupRequest(
  sessionId: string,
  input: { restaurantName: string; googlePlaceId: string | null; address: string | null },
): Promise<SignupStatus> {
  return serverFetch<SignupStatus>(
    `/api/billing/signup-checkout/${encodeURIComponent(sessionId)}/request`,
    {
      method: 'POST',
      body: JSON.stringify({
        restaurant_name: input.restaurantName,
        google_place_id: input.googlePlaceId,
        address: input.address,
      }),
    },
  )
}

const NO_SIGNUP: SignupStatus = {
  state: 'none',
  plan: null,
  checkout_session_id: null,
  restaurant_name: null,
  google_place_id: null,
  address: null,
  requested_at: null,
  reason: null,
  refunded: false,
}

/**
 * Read on every login with no team. A server without the route (an older
 * deploy) or out of reach answers `none`, which is the wizard as it was:
 * the gate fails open to the plan cards, never to a blank screen.
 */
export async function fetchSignupStatus(personalPubkey: string): Promise<SignupStatus> {
  try {
    return await serverFetch<SignupStatus>(
      `/api/billing/signup/${encodeURIComponent(personalPubkey)}`,
    )
  } catch (err) {
    if (err instanceof ApiError && FAIL_OPEN.has(err.code)) return NO_SIGNUP
    if (!(err instanceof ApiError)) return NO_SIGNUP
    throw err
  }
}

/** Codes that mean "the server could not say", not "the server said no". */
const FAIL_OPEN = new Set(['not_found', 'server_error', 'network_error'])

export function fetchPendingPurchase(personalPubkey: string): Promise<{ paid: boolean }> {
  return serverFetch<{ paid: boolean }>(
    `/api/billing/signup-checkout/pending/${encodeURIComponent(personalPubkey)}`,
  )
}

export function createSignupPortal(sessionId: string): Promise<{ url: string }> {
  return serverFetch<{ url: string }>(
    `/api/billing/signup-checkout/${encodeURIComponent(sessionId)}/portal`,
    { method: 'POST' },
  )
}

// ── Restaurant half: owner-gated ─────────────────────────────────────────

async function withMerchantSession<T>(
  ctx: LoyaltyServiceContext,
  call: () => Promise<T>,
): Promise<T> {
  try {
    return await call()
  } catch (err) {
    if (!(err instanceof ApiError) || err.code !== 'unauthorized') throw err
    await ensureMerchantSession(ctx.restaurantPubkey, ctx.signAsRestaurant)
    return call()
  }
}

export function fetchPlanSnapshot(ctx: LoyaltyServiceContext): Promise<PlanSnapshot> {
  return withMerchantSession(ctx, () =>
    serverFetch<PlanSnapshot>(`/api/billing/${ctx.restaurantPubkey}`),
  )
}

/** Move the person's pending purchase onto this restaurant (§4.3). */
export function claimPurchase(
  ctx: LoyaltyServiceContext,
  personalPubkey: string,
): Promise<PlanSnapshot> {
  return withMerchantSession(ctx, () =>
    serverFetch<PlanSnapshot>(`/api/billing/${ctx.restaurantPubkey}/claim`, {
      method: 'POST',
      body: JSON.stringify({ personal_pubkey: personalPubkey }),
    }),
  )
}

export function createUpgradeCheckout(
  ctx: LoyaltyServiceContext,
  interval: BillingInterval,
  email: string | null,
): Promise<{ url: string }> {
  return withMerchantSession(ctx, () =>
    serverFetch<{ url: string }>(`/api/billing/${ctx.restaurantPubkey}/checkout-session`, {
      method: 'POST',
      body: JSON.stringify({ interval, email }),
    }),
  )
}

export function createPortalSession(ctx: LoyaltyServiceContext): Promise<{ url: string }> {
  return withMerchantSession(ctx, () =>
    serverFetch<{ url: string }>(`/api/billing/${ctx.restaurantPubkey}/portal-session`, {
      method: 'POST',
    }),
  )
}

/**
 * Cancel the restaurant's subscription now — the delete-restaurant flow calls
 * this before the Keycast team goes, so a deleted restaurant never keeps
 * billing. A restaurant without a subscription answers 200 with its plan.
 */
export function cancelSubscription(ctx: LoyaltyServiceContext): Promise<PlanSnapshot> {
  return withMerchantSession(ctx, () =>
    serverFetch<PlanSnapshot>(`/api/billing/${ctx.restaurantPubkey}/subscription`, {
      method: 'DELETE',
    }),
  )
}
